import {useMemo, useRef, useState} from "react";
import {Alert, Anchor, Checkbox, Text, Transition} from "@mantine/core";
import {useLocalStorage} from "@mantine/hooks";
import {useTranslation, Trans} from "react-i18next";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import {bffApi} from "../../chat/bffApi";
import {ResourceType} from "../types";
import {ChecklistModalActions} from "../checklist/ChecklistModalActions";
import {DocumentsList, TemplateSelect} from "./parts";
import {setStoredTemplate, setStoredTopicTemplate} from "./templateStorage";
import type {
    CollectionMeta,
    ExportDocument,
    ModalBusyProps,
    TopicMeta,
} from "./types";
import classes from "../checklist/modalStyles.module.css";

const DFLOW_URL = "https://app.dflow.com/documents";

// The locale files use the "<a-1>" tag name for the link in these phrases.
const DFLOW_LINK_TAG = "a-1";

type Props = {
    onClose: () => void;
    topicMeta: TopicMeta;
    collectionMeta?: CollectionMeta;
    documents: ExportDocument[];
} & ModalBusyProps;

export const SendToDflowTab = ({
    onClose,
    topicMeta,
    collectionMeta,
    documents,
    busy,
    onBusyChange,
}: Props) => {
    const {t} = useTranslation();
    const {data: home} = dibAppApi.endpoints.getHome.useQuery();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const isdemo = !!user?.demokonto;
    const templates = home?.templates ?? [];

    const [finalDocuments, setFinalDocuments] = useState(documents);
    const count = useMemo(
        () => finalDocuments.filter((d) => d.selected).length,
        [finalDocuments]
    );
    const [selectedTemplate, setSelectedTemplate] = useState<string>();
    const [validTemplate, setValidTemplate] = useState(true);
    const [combine, setCombine] = useState(false);
    const [combinedUnderstood, setCombinedUnderstood] = useLocalStorage({
        key: "merge-understood-confirm",
        defaultValue: false,
    });
    const templateRef = useRef<HTMLDivElement>(null);

    // RTK's `isLoading` is not the in-flight signal: it stays false on a
    // retry after an error. `busy` is set around the trigger instead.
    const [sendToDflow, {isSuccess, isError}] =
        dibAppApi.endpoints.sendToDflow.useLazyQuery();
    const [trackDocument] = bffApi.endpoints.trackDocument.useMutation();

    const isStandalone =
        topicMeta.resourceTypeId === ResourceType.documentGenerator ||
        topicMeta.resourceTypeId === ResourceType.byggekloss;

    const handleTemplateSelect = (id: string) => {
        setSelectedTemplate(id);
        setValidTemplate(true);
    };

    const runSend = () => {
        if (isdemo) return;
        if (selectedTemplate === undefined && templates.length !== 0) return;

        const topics = isStandalone
            ? topicMeta.resourceId
            : finalDocuments
                  .filter((d) => d.selected)
                  .map((d) => d.id)
                  .join(";");

        setStoredTemplate(selectedTemplate);
        setStoredTopicTemplate(topicMeta.topicId, selectedTemplate);

        onBusyChange(true);
        sendToDflow({
            topicId: isStandalone ? undefined : topicMeta.resourceId,
            collectionId: isStandalone ? undefined : collectionMeta?.id,
            topics,
            folderName: null,
            template: selectedTemplate ?? "0",
            combine,
        })
            .unwrap()
            .then(() => {
                // Count confirmed transfers only, never attempts.
                trackDocument({
                    action: "sent_to_dflow",
                    value: isStandalone ? 1 : count,
                    documentKey: topicMeta.resourceId,
                    mode: isStandalone ? "standalone" : "collection",
                    combine,
                    templateId: selectedTemplate ?? "0",
                });
            })
            .catch(() => {
                // Transfer failed — the UI already reflects isError; no metric.
            })
            // The tab stays open on both outcomes, so it always clears busy.
            .finally(() => onBusyChange(false));
    };

    const handlePrimaryClick = () => {
        if (selectedTemplate === undefined && templates.length !== 0) {
            setValidTemplate(false);
            templateRef.current?.scrollIntoView({
                behavior: "auto",
                block: "center",
                inline: "nearest",
            });
            return;
        }
        runSend();
    };

    const primaryDisabled =
        isdemo ||
        selectedTemplate === undefined ||
        (!isStandalone && !finalDocuments.some((d) => d.selected)) ||
        isSuccess;

    return (
        <>
            <div className={classes.modalBody}>
                {isdemo && (
                    <Alert variant="light" color="gray">
                        {t("Info.GenerationNotAvailableForDemoUsers")}
                    </Alert>
                )}
                {isSuccess && (
                    <Alert variant="light" color="blue">
                        <Trans
                            i18nKey="Phrase.DocumentsAvailableInDflow"
                            count={isStandalone ? 1 : count}
                            components={{
                                [DFLOW_LINK_TAG]: (
                                    <Anchor
                                        href={DFLOW_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                ),
                            }}
                        />
                    </Alert>
                )}
                {isError && (
                    <Alert variant="light" color="red">
                        {t("Error.SomethingWentWrong")}
                    </Alert>
                )}
                {isStandalone ? (
                    <Text fw={600} className={classes.modalText}>
                        {topicMeta.name}
                    </Text>
                ) : (
                    <DocumentsList
                        count={count}
                        documents={finalDocuments}
                        setDocuments={setFinalDocuments}
                    />
                )}
                <div ref={templateRef}>
                    <TemplateSelect
                        topicId={topicMeta.topicId}
                        selectedTemplate={selectedTemplate}
                        onSelect={handleTemplateSelect}
                        error={!validTemplate}
                    />
                </div>
                <Checkbox
                    color="gneis.9"
                    disabled={count < 2}
                    checked={combine}
                    onChange={(e) => setCombine(e.currentTarget.checked)}
                    label={t("Info.MergeDocuments")}
                    classNames={{
                        root: classes.modalRow,
                        labelWrapper: classes.modalRowLabelWrapper,
                        label: classes.modalRowLabel,
                    }}
                />
                <Transition
                    mounted={combine && !combinedUnderstood}
                    transition="fade"
                    duration={400}
                    timingFunction="ease"
                >
                    {(styles) => (
                        <Alert
                            variant="light"
                            color="blue"
                            title={t("alert.MergeConfirmTitle")}
                            withCloseButton
                            onClose={() => setCombinedUnderstood(true)}
                            style={styles}
                        >
                            <Text>{t("alert.MergeConfirmBody")}</Text>
                        </Alert>
                    )}
                </Transition>
            </div>
            <ChecklistModalActions
                primary={{
                    label: t("Phrase.SendToDflow"),
                    onClick: handlePrimaryClick,
                    loading: busy,
                    disabled: primaryDisabled,
                }}
                secondary={{
                    label: t("Word.Close"),
                    onClick: onClose,
                    disabled: busy,
                }}
            />
        </>
    );
};
